/**
 * Utility functions for handling provisioning flags in Arena Creation
 */

/**
 * Parses a comma-separated string of provisioning flags into an array
 * @param flagsString - Comma-separated string of flags
 * @returns Array of trimmed, non-empty flags
 */
export function parseProvisioningFlags(flagsString: string | undefined): string[] {
  if (!flagsString || typeof flagsString !== "string") {
    return [];
  }

  return flagsString
    .split(",")
    .map((flag) => flag.trim())
    .filter((flag) => flag.length > 0);
}

/**
 * Validates provisioning flags format
 * @param flagsString - Comma-separated string of flags
 * @returns Object with validation result and error message if invalid
 */
export function validateProvisioningFlags(flagsString: string): {
  isValid: boolean;
  errorMessage?: string;
} {
  if (!flagsString.trim()) {
    return { isValid: true }; // Empty string is valid (optional field)
  }

  const flags = parseProvisioningFlags(flagsString);

  // Check for empty flags after splitting
  const hasEmptyFlags = flagsString.split(",").some((flag) => !flag.trim());
  if (hasEmptyFlags) {
    return {
      isValid: false,
      errorMessage:
        "Provisioning flags cannot contain empty values. Please remove extra commas.",
    };
  }

  // Check for valid flag format (alphanumeric, underscores, hyphens)
  const invalidFlags = flags.filter((flag) => !/^[a-zA-Z0-9_-]+$/.test(flag));
  if (invalidFlags.length > 0) {
    return {
      isValid: false,
      errorMessage: `Invalid flag format: "${invalidFlags[0]}". Flags can only contain letters, numbers, underscores, and hyphens.`,
    };
  }

  return { isValid: true };
}

/**
 * Formats provisioning flags for display
 * @param flagsString - Comma-separated string of flags
 * @returns Formatted string with proper spacing
 */
export function formatProvisioningFlags(flagsString: string): string {
  const flags = parseProvisioningFlags(flagsString);
  return flags.join(", ");
}
